import { css } from "@emotion/css";
import { text, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import * as React from "react";
import { resetStyles } from "../../../utils";
import { Callout } from "../../Callout";
import { Divider } from "../../Divider/index";
import { EmptyStateDefaultIllustration } from "../../Illustrations";
import type { PrimaryPageAction, PageAction, PageActionsProps } from "../../PageActions";
import { PageActionComponent, toInternalPageAction, toInternalPrimaryPageAction } from "../../PageActions";
interface EmptyStateOnboardingProps {
    title: string;
    description?: React.ReactNode | string;
    secondaryDescription?: React.ReactNode | string;
    image?: React.ReactElement;
    learnMore?: React.ReactElement;
    primaryAction?: PrimaryPageAction;
    actions?: PageAction[];
    callout?: EmptyStateOnboardingCallout;
}
export interface EmptyStateOnboardingCallout {
    title: string;
    description: string;
    action: PageAction;
}
export function EmptyStateOnboarding({ title, description, secondaryDescription, image, primaryAction, actions, learnMore, callout }: EmptyStateOnboardingProps) {
    return (<div className={pageContainerStyles.container}>
            <div className={pageContentStyles.container}>
                <div className={pageContentStyles.content}>
                    {title && (<>
                            <h2 className={pageContentStyles.header}>{title}</h2>
                            <div className={pageContentStyles.descriptionAndActions}>
                                {description && <div className={pageContentStyles.description}>{description}</div>}
                                {callout && (<Callout type={"information"} title={callout.title}>
                                        <p className={pageContentStyles.calloutDescription}>{callout.description}</p>
                                        <PageActions actions={[callout.action]}/>
                                    </Callout>)}

                                <div className={pageContentStyles.actions}>
                                    <PageActions primaryAction={primaryAction} actions={actions}/>

                                    {learnMore && learnMore}
                                </div>
                                {secondaryDescription ? (<>
                                        <div className={pageContentStyles.divider}>
                                            <Divider />
                                        </div>
                                        <div className={pageContentStyles.secondaryDescription}>{secondaryDescription}</div>
                                    </>) : null}
                            </div>
                        </>)}
                </div>
                <div className={pageContentStyles.illustrationWrapperStyles}>{image ?? <EmptyStateDefaultIllustration />}</div>
            </div>
        </div>);
}
function PageActions({ primaryAction, actions }: PageActionsProps) {
    const hasPrimaryAction = primaryAction !== undefined;
    const actionButtons = actions?.map(toInternalPageAction);
    if ((!actions || actions.length === 0) && !hasPrimaryAction) {
        return null;
    }
    return (<>
            {hasPrimaryAction && <PageActionComponent key="primary" item={toInternalPrimaryPageAction(primaryAction)}/>}
            {actionButtons && actionButtons.map((item, id) => <PageActionComponent key={id} item={item}/>)}
        </>);
}
const pageContainerStyles = {
    container: css({
        font: text.body.regular.medium,
        margin: space[32],
        display: "grid",
        height: `calc(100% - (${space[32]} * 2))`, // minus the top and bottom margin
        "@media (max-width: 1088px)": {
            alignContent: "start",
            textAlign: "center",
        },
    }),
};
const pageContentStyles = {
    container: css({
        display: "grid",
        gap: space[80],
        gridTemplateColumns: "2fr 2fr",
        alignItems: "center",
        justifyItems: "end",
        margin: "0 auto",
        "@media (max-width: 1488px)": {
            gap: space[64],
        },
        "@media (max-width: 1088px)": {
            justifyItems: "center",
            gridTemplateColumns: "1fr",
            gap: space[40],
        },
    }),
    illustrationWrapperStyles: css({
        width: "100%",
        "@media (max-width: 1088px)": {
            alignSelf: "end",
            order: "-1",
            maxWidth: "250px",
        },
    }),
    content: css({
        maxWidth: "536px",
        "@media (max-width: 1088px)": {
            alignSelf: "start",
        },
    }),
    header: css({
        font: text.heading.large,
        color: themeTokens.color.text.primary,
        margin: 0,
        marginBottom: space[16],
    }),
    descriptionAndActions: css({
        display: "flex",
        flexDirection: "column",
        gap: space[24],
    }),
    description: css({
        font: text.body.regular.large,
        color: themeTokens.color.text.secondary,
    }),
    calloutDescription: css({
        ...resetStyles.paragraph,
        marginBottom: space[12],
        textAlign: "left",
    }),
    divider: css({
        marginBottom: space[16],
    }),
    secondaryDescription: css({
        font: text.body.regular.medium,
        color: themeTokens.color.text.secondary,
    }),
    actions: css({
        display: "flex",
        flexWrap: "wrap",
        gap: space[16],
        alignItems: "center",
        "@media (max-width: 1088px)": {
            justifyContent: "center",
            flexWrap: "wrap",
        },
    }),
};
